import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Button, Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import i18n from 'i18n-js';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';
import {
  ChangeVehicleStatusModal,
  ChangeBrokersModal,
  ChangeServicesModal,
} from 'components/Modals';
import {
  VehicleDetailsWidget,
  Telemetry,
  Services,
  VehicleLogs,
  AssignedGroupsWidget,
  Statuses,
  VehicleImages,
} from 'components/Vehicles';
import Can from 'components/Can';
import Brokers from 'components/Brokers';
import Feature from 'components/Feature';
import { AlarmsWidget } from 'components/Alarms';
import OrgAbility from 'components/OrgAbility';
import { withRouter, useParams } from 'react-router-dom';
import { UserContext } from 'context';
import DocumentsWidget from 'components/Documents/Widget';
import { ReservationsWidget } from 'components/Reservations';

const VehicleDetails = ({ history }) => {
  const { id } = useParams();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const dispatch = useDispatch();
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openChangeServicesModal, setOpenChangeServicesModal] = useState(false);
  const [openChangeBrokersModal, setOpenChangeBrokersModal] = useState(false);

  const vehicle = useSelector((state) => state.vehicles.vehicle);
  const orgs = useSelector((state) => state.vehicles.orgs);
  const fetchedChangeBrokersSuccess = useSelector(
    (state) => state.vehicles.fetchedChangeBrokersSuccess
  );
  const fetchedChangeServicesSuccess = useSelector(
    (state) => state.vehicles.fetchedChangeServicesSuccess
  );
  const steps = useSelector((state) => state.vehicles.onboarding.steps);

  const { orgType } = useContext(UserContext);

  const handleStatusModalOpen = () => {
    dispatch(vehicleActions.resetChangeStatus());
    setOpenStatusModal((prevState) => !prevState);
  };

  const handleServicesModalOpen = () => {
    setOpenChangeServicesModal((prevState) => !prevState);
  };

  const handleBrokersModalOpen = () => {
    setOpenChangeBrokersModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleById(id));
      dispatch(vehicleActions.fetchVehicleOrgsById(id));
    }
    dispatch(vehicleActions.resetOnboarding());

    return () => {
      dispatch(vehicleActions.resetVehicle());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (fetchedChangeBrokersSuccess || fetchedChangeServicesSuccess) {
      dispatch(vehicleActions.fetchVehicleById(id));
      dispatch(vehicleActions.fetchVehicleOrgsById(id));
    }
  }, [id, dispatch, fetchedChangeBrokersSuccess, fetchedChangeServicesSuccess]);

  /**
   * Broker menu list
   * It has the label and the function for each action
   */
  let brokerItems = [];
  let servicesItems = [];

  if (isSmUp) {
    brokerItems =
      orgType === 'msOwnerOrg'
        ? [
            {
              title: i18n.t('vehicle.edit_broker'),
              onClick: handleBrokersModalOpen,
              can: { action: 'update', name: 'vehicle' },
            },
          ]
        : [];
    servicesItems = [
      {
        title: i18n.t('vehicle.edit_services'),
        onClick: handleServicesModalOpen,
        can: { action: 'update', name: 'vehicle' },
      },
    ];
  }

  return (
    <Layout title={i18n.t('vehicle.details_title')}>
      <Container maxWidth={false} disableGutters>
        <Box>
          <Box
            sx={{
              mb: 4.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Title title={i18n.t('vehicle.details_title')} />
            <Button
              color="primary"
              variant="contained"
              onClick={handleStatusModalOpen}
              sx={{ textTransform: 'inherit' }}
              data-cy="change-status"
            >
              {i18n.t('vehicle.change_status')}
            </Button>
          </Box>
          <Grid container spacing={2} sx={{ m: 0, width: '100%' }}>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <Stack spacing={2}>
                <VehicleDetailsWidget id={id} vehicle={vehicle} />
                <VehicleImages id={vehicle?.id} />
                <Statuses id={parseInt(id)} />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <Stack spacing={2}>
                <Feature name="telemetry" is="enabled">
                  <Telemetry vehicleId={id} timezone={vehicle?.vehicle_group?.time_zone} />
                </Feature>
                <Can I="read" a="document">
                  <DocumentsWidget type="Vehicle" id={parseInt(id)} />
                </Can>
                <OrgAbility subject={['msOwnerOrg']}>
                  <Services id={id} services={orgs} menuOptions={servicesItems} />
                </OrgAbility>
                <Can I="read" on="vehicle">
                  <Brokers
                    id={parseInt(id, 10)}
                    type="vehicle"
                    menuOptions={brokerItems}
                    brokers={vehicle?.brokers || {}}
                  />
                </Can>
                <AssignedGroupsWidget
                  vehicle_id={vehicle.id}
                  vehicle_group={
                    vehicle.vehicle_group ||
                    vehicle.customer_vehicle_group ||
                    vehicle.owner_vehicle_group ||
                    {}
                  }
                  params={{
                    parent_id:
                      vehicle?.vehicle_group?.parent?.id ||
                      vehicle?.customer_vehicle_group?.parent?.id ||
                      vehicle?.owner_vehicle_group?.parent?.id,
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <Stack spacing={2}>
                <Feature name="reservation" is="enabled">
                  <ReservationsWidget
                    type="vehicle"
                    history={history}
                    customParams={{
                      vehicle_id: id,
                      statuses: ['RESERVED', 'INPROGRESS', 'OFFLINE', 'COMPLETED', 'NOSHOW'],
                    }}
                  />
                </Feature>
                <Can I="read" a="conditional_instance">
                  <AlarmsWidget type="vehicle" id={parseInt(id)} />
                </Can>
                <Can I="read" on="event_log">
                  <VehicleLogs id={id} />
                </Can>
                <Comments type="vehicle" id={parseInt(id)} />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>
      {openStatusModal && (
        <ChangeVehicleStatusModal
          open={true}
          vehicleId={parseInt(id)}
          vehicle={vehicle}
          handleClick={handleStatusModalOpen}
        />
      )}
      {openChangeServicesModal && (
        <ChangeServicesModal
          open={true}
          vehicle={vehicle}
          step={steps?.services}
          handleClick={handleServicesModalOpen}
          orgs={orgs}
        />
      )}
      {openChangeBrokersModal && (
        <ChangeBrokersModal
          open={true}
          source="vehicle"
          vehicle={vehicle}
          handleClick={handleBrokersModalOpen}
        />
      )}
    </Layout>
  );
};

VehicleDetails.propTypes = {
  history: PropTypes.object.isRequired,
};
export default withRouter(VehicleDetails);
